import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Settings as SettingsIcon, Save, RefreshCw, Database, Bell, Sliders, MapPin, Plus, Trash2, Edit2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { villagesAPI } from '../../services/api';
import ChangePassword from './ChangePassword';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Settings = ({ villageData, setVillageData, onDataChange }) => {
  // New village form
  const [newVillage, setNewVillage] = useState({
    name: '',
    wtps: 0,
    ohts: 0,
    dmas: 0,
    households: 0,
    nrw: 0,
  });

  const [isAddingVillage, setIsAddingVillage] = useState(false);
  const [editingVillage, setEditingVillage] = useState(null);

  // Water Quality Parameters
  const [qualityParams, setQualityParams] = useState({
    ph: { min: 6.5, max: 8.5, current: 7.3 },
    turbidity: { min: 0, max: 1, current: 0.8 },
    tds: { min: 0, max: 500, current: 430 },
    chlorine: { min: 0.2, max: 1.0, current: 0.4 },
    fluoride: { min: 0, max: 1.5, current: 0.9 },
  });

  // NRW Thresholds
  const [nrwThresholds, setNrwThresholds] = useState({
    excellent: 10.0,
    good: 12.0,
    fair: 15.0,
    target: 10.0,
  });

  // LPCD Settings
  const [lpcdSettings, setLpcdSettings] = useState({
    target: 100.0,
    minimum: 55.0,
    persons_per_household: 4,
  });

  // Alert Settings
  const [alertSettings, setAlertSettings] = useState({
    enable_nrw_alerts: true,
    enable_quality_alerts: true,
    enable_maintenance_alerts: true,
    enable_leak_alerts: true,
    nrw_alert_threshold: 12.0,
    quality_alert_delay_hours: 2,
    maintenance_notification_days: 7,
  });

  const [isLoadingSettings, setIsLoadingSettings] = useState(false);

  // Load system settings from backend
  const loadSystemSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      const result = await response.json();
      
      if (result.success && result.data) {
        if (result.data.nrw_thresholds) {
          setNrwThresholds(result.data.nrw_thresholds);
        }
        if (result.data.lpcd_settings) {
          setLpcdSettings(result.data.lpcd_settings);
        }
        if (result.data.alert_settings) {
          setAlertSettings(result.data.alert_settings);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load system settings');
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    loadSystemSettings();
  }, [loadSystemSettings]);

  // Legacy alert settings (kept for compatibility)
  const [oldAlertSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    criticalAlerts: true,
    warningAlerts: true,
    qualityAlerts: true,
    nrwAlerts: true,
  });

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    refreshInterval: 300, // seconds
    dataRetention: 90, // days
    autoBackup: true,
    maintenanceMode: false,
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleVillageDataChange = (village, field, value) => {
    setVillageData(prev => ({
      ...prev,
      [village]: {
        ...prev[village],
        [field]: field === 'name' ? value : (parseFloat(value) || 0)
      }
    }));
  };

  const handleAddVillage = async () => {
    if (!newVillage.name.trim()) {
      toast.error('Please enter a village name');
      return;
    }

    const villageKey = newVillage.name.toLowerCase().replace(/\s+/g, '');
    
    if (villageData[villageKey]) {
      toast.error('Village already exists');
      return;
    }

    setIsLoading(true);
    try {
      await villagesAPI.add(newVillage);
      
      setNewVillage({
        name: '',
        wtps: 0,
        ohts: 0,
        dmas: 0,
        households: 0,
        nrw: 0,
      });

      setIsAddingVillage(false);
      toast.success(`Village "${newVillage.name}" added successfully!`, {
        description: 'The new village is now available in all sections.'
      });

      // Refresh village data from backend
      await onDataChange();
    } catch (error) {
      toast.error('Failed to add village', {
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVillage = async (villageKey, villageName) => {
    if (Object.keys(villageData).length <= 1) {
      toast.error('Cannot delete the last village');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to delete "${villageName}"?`);
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await villagesAPI.delete(villageKey);
      
      toast.success(`Village "${villageName}" deleted successfully`, {
        description: 'The village has been removed from all sections.'
      });

      // Refresh village data from backend
      await onDataChange();
    } catch (error) {
      toast.error('Failed to delete village', {
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditVillageName = async (villageKey, newName) => {
    if (!newName.trim()) {
      toast.error('Village name cannot be empty');
      return;
    }

    setIsLoading(true);
    try {
      await villagesAPI.update(villageKey, { name: newName });
      
      setEditingVillage(null);
      toast.success('Village name updated successfully');

      // Refresh village data from backend
      await onDataChange();
    } catch (error) {
      toast.error('Failed to update village name', {
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQualityParamChange = (param, field, value) => {
    setQualityParams(prev => ({
      ...prev,
      [param]: {
        ...prev[param],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleNRWThresholdChange = (field, value) => {
    setNrwThresholds(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleSaveSettings = async (section) => {
    if (section === 'Village Data') {
      setIsLoading(true);
      try {
        const updatePromises = Object.entries(villageData).map(([villageKey, data]) => 
          villagesAPI.update(villageKey, {
            name: data.name,
            wtps: data.wtps,
            ohts: data.ohts,
            dmas: data.dmas,
            households: data.households,
            nrw: data.nrw
          })
        );

        await Promise.all(updatePromises);

        toast.success('Village Data settings saved successfully!', {
          description: 'All changes have been saved to the database.',
        });

        await onDataChange();
      } catch (error) {
        toast.error('Failed to save village data', {
          description: error.message
        });
      } finally {
        setIsLoading(false);
      }
    } else if (section === 'NRW Thresholds') {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/settings/nrw-thresholds`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nrwThresholds)
        });
        const result = await response.json();
        
        if (result.success) {
          toast.success('NRW Thresholds saved successfully!', {
            description: 'Thresholds will be used for analytics and alerts.',
          });
        } else {
          throw new Error('Failed to save');
        }
      } catch (error) {
        toast.error('Failed to save NRW thresholds');
      } finally {
        setIsLoading(false);
      }
    } else if (section === 'LPCD Settings') {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/settings/lpcd`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(lpcdSettings)
        });
        const result = await response.json();
        
        if (result.success) {
          toast.success('LPCD Settings saved successfully!', {
            description: 'Settings will be used for demand calculations.',
          });
        } else {
          throw new Error('Failed to save');
        }
      } catch (error) {
        toast.error('Failed to save LPCD settings');
      } finally {
        setIsLoading(false);
      }
    } else if (section === 'Alert Settings') {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/settings/alerts`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(alertSettings)
        });
        const result = await response.json();
        
        if (result.success) {
          toast.success('Alert Settings saved successfully!', {
            description: 'Alert configuration updated for demos and monitoring.',
          });
        } else {
          throw new Error('Failed to save');
        }
      } catch (error) {
        toast.error('Failed to save alert settings');
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.success(`${section} settings saved successfully!`, {
        description: 'Changes have been applied to the system.',
      });
    }
  };

  const handleResetSettings = (section) => {
    toast.info(`${section} settings reset to defaults`, {
      description: 'All changes have been reverted.',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Settings</h1>
        <p className="text-muted-foreground">Configure system parameters, thresholds, and data management</p>
      </div>

      <Tabs defaultValue="village" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6">
          <TabsTrigger value="village" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Village Data
          </TabsTrigger>
          <TabsTrigger value="quality" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Water Quality
          </TabsTrigger>
          <TabsTrigger value="nrw" className="flex items-center gap-2">
            <Sliders className="w-4 h-4" />
            NRW Thresholds
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Alerts
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" />
            System
          </TabsTrigger>
        </TabsList>

        {/* Village Data Settings */}
        <TabsContent value="village" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Village Management - Salcette Block</CardTitle>
                  <CardDescription>Add, modify, or remove villages and update their infrastructure data</CardDescription>
                </div>
                <Button onClick={() => setIsAddingVillage(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Village
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add New Village Form */}
              {isAddingVillage && (
                <div className="p-4 bg-primary/5 rounded-lg border-2 border-primary/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Add New Village</h3>
                    <Button variant="ghost" size="sm" onClick={() => setIsAddingVillage(false)}>
                      Cancel
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                      <Label htmlFor="new-village-name">Village Name *</Label>
                      <Input
                        id="new-village-name"
                        value={newVillage.name}
                        onChange={(e) => setNewVillage(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-village-wtps">WTPs</Label>
                      <Input
                        id="new-village-wtps"
                        type="number"
                        value={newVillage.wtps}
                        onChange={(e) => setNewVillage(prev => ({ ...prev, wtps: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-village-ohts">OHTs</Label>
                      <Input
                        id="new-village-ohts"
                        type="number"
                        value={newVillage.ohts}
                        onChange={(e) => setNewVillage(prev => ({ ...prev, ohts: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-village-dmas">DMAs</Label>
                      <Input
                        id="new-village-dmas"
                        type="number"
                        value={newVillage.dmas}
                        onChange={(e) => setNewVillage(prev => ({ ...prev, dmas: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-village-households">Households</Label>
                      <Input
                        id="new-village-households"
                        type="number"
                        value={newVillage.households}
                        onChange={(e) => setNewVillage(prev => ({ ...prev, households: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-village-nrw">NRW %</Label>
                      <Input
                        id="new-village-nrw"
                        type="number"
                        step="0.1"
                        value={newVillage.nrw}
                        onChange={(e) => setNewVillage(prev => ({ ...prev, nrw: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button onClick={handleAddVillage} disabled={isLoading}>
                      <Plus className="w-4 h-4 mr-2" />
                      {isLoading ? 'Adding...' : 'Add Village'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Existing Villages */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Badge variant="outline">{Object.keys(villageData).length} villages</Badge>
                  <span>in Salcette Block</span>
                </div>
                {Object.entries(villageData).map(([villageKey, data]) => (
                  <div key={villageKey} className="p-4 bg-muted/30 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-4">
                      {editingVillage === villageKey ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={data.name}
                            onChange={(e) => handleVillageDataChange(villageKey, 'name', e.target.value)}
                            className="w-48"
                          />
                          <Button size="sm" onClick={() => handleEditVillageName(villageKey, data.name)}>
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingVillage(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold">{data.name}</h3>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setEditingVillage(villageKey)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDeleteVillage(villageKey, data.name)}
                        disabled={isLoading}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {isLoading ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      <div>
                        <Label htmlFor={`${villageKey}-wtps`}>WTPs</Label>
                        <Input
                          id={`${villageKey}-wtps`}
                          type="number"
                          value={data.wtps}
                          onChange={(e) => handleVillageDataChange(villageKey, 'wtps', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${villageKey}-ohts`}>OHTs</Label>
                        <Input
                          id={`${villageKey}-ohts`}
                          type="number"
                          value={data.ohts}
                          onChange={(e) => handleVillageDataChange(villageKey, 'ohts', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${villageKey}-dmas`}>DMAs</Label>
                        <Input
                          id={`${villageKey}-dmas`}
                          type="number"
                          value={data.dmas}
                          onChange={(e) => handleVillageDataChange(villageKey, 'dmas', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${villageKey}-households`}>Households</Label>
                        <Input
                          id={`${villageKey}-households`}
                          type="number"
                          value={data.households}
                          onChange={(e) => handleVillageDataChange(villageKey, 'households', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${villageKey}-nrw`}>NRW %</Label>
                        <Input
                          id={`${villageKey}-nrw`}
                          type="number"
                          step="0.1"
                          value={data.nrw}
                          onChange={(e) => handleVillageDataChange(villageKey, 'nrw', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button onClick={() => handleSaveSettings('Village Data')} disabled={isLoading}>
                  <Save className="w-4 h-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => handleResetSettings('Village Data')} disabled={isLoading}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Water Quality Settings - keeping the rest of the tabs unchanged */}
        <TabsContent value="quality" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Water Quality Parameters</CardTitle>
              <CardDescription>Configure acceptable ranges and current values for water quality testing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(qualityParams).map(([param, values]) => (
                <div key={param} className="p-4 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold capitalize">{param}</h3>
                    <Badge variant={values.current >= values.min && values.current <= values.max ? 'default' : 'destructive'}>
                      {values.current >= values.min && values.current <= values.max ? 'Within Range' : 'Out of Range'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor={`${param}-min`}>Minimum Value</Label>
                      <Input
                        id={`${param}-min`}
                        type="number"
                        step="0.1"
                        value={values.min}
                        onChange={(e) => handleQualityParamChange(param, 'min', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`${param}-max`}>Maximum Value</Label>
                      <Input
                        id={`${param}-max`}
                        type="number"
                        step="0.1"
                        value={values.max}
                        onChange={(e) => handleQualityParamChange(param, 'max', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`${param}-current`}>Current Value</Label>
                      <Input
                        id={`${param}-current`}
                        type="number"
                        step="0.1"
                        value={values.current}
                        onChange={(e) => handleQualityParamChange(param, 'current', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex gap-3">
                <Button onClick={() => handleSaveSettings('Water Quality')}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => handleResetSettings('Water Quality')}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NRW Thresholds Settings */}
        <TabsContent value="nrw" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>NRW (Non-Revenue Water) Thresholds</CardTitle>
              <CardDescription>Configure thresholds for NRW performance classification and alerts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nrw-target">Target NRW (%)</Label>
                  <Input
                    id="nrw-target"
                    type="number"
                    step="0.1"
                    value={nrwThresholds.target}
                    onChange={(e) => setNrwThresholds({...nrwThresholds, target: parseFloat(e.target.value)})}
                  />
                  <p className="text-xs text-muted-foreground">Goal percentage for NRW reduction</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nrw-excellent">Excellent Threshold (%)</Label>
                  <Input
                    id="nrw-excellent"
                    type="number"
                    step="0.1"
                    value={nrwThresholds.excellent}
                    onChange={(e) => setNrwThresholds({...nrwThresholds, excellent: parseFloat(e.target.value)})}
                  />
                  <p className="text-xs text-muted-foreground">Below this is excellent performance</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nrw-good">Good Threshold (%)</Label>
                  <Input
                    id="nrw-good"
                    type="number"
                    step="0.1"
                    value={nrwThresholds.good}
                    onChange={(e) => setNrwThresholds({...nrwThresholds, good: parseFloat(e.target.value)})}
                  />
                  <p className="text-xs text-muted-foreground">Below this is good performance</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nrw-fair">Fair Threshold (%)</Label>
                  <Input
                    id="nrw-fair"
                    type="number"
                    step="0.1"
                    value={nrwThresholds.fair}
                    onChange={(e) => setNrwThresholds({...nrwThresholds, fair: parseFloat(e.target.value)})}
                  />
                  <p className="text-xs text-muted-foreground">Below this is fair, above is poor</p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Classification Preview</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-500">Excellent</Badge>
                    <span className="text-muted-foreground">NRW &lt; {nrwThresholds.excellent}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-500">Good</Badge>
                    <span className="text-muted-foreground">{nrwThresholds.excellent}% ≤ NRW &lt; {nrwThresholds.good}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-yellow-500">Fair</Badge>
                    <span className="text-muted-foreground">{nrwThresholds.good}% ≤ NRW &lt; {nrwThresholds.fair}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-500">Poor</Badge>
                    <span className="text-muted-foreground">NRW ≥ {nrwThresholds.fair}%</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
                <h4 className="font-medium">LPCD (Liters Per Capita Per Day) Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lpcd-target">Target LPCD</Label>
                    <Input
                      id="lpcd-target"
                      type="number"
                      value={lpcdSettings.target}
                      onChange={(e) => setLpcdSettings({...lpcdSettings, target: parseFloat(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">Standard water supply target</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lpcd-minimum">Minimum LPCD</Label>
                    <Input
                      id="lpcd-minimum"
                      type="number"
                      value={lpcdSettings.minimum}
                      onChange={(e) => setLpcdSettings({...lpcdSettings, minimum: parseFloat(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">Minimum acceptable supply</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="persons-household">Persons per Household</Label>
                    <Input
                      id="persons-household"
                      type="number"
                      value={lpcdSettings.persons_per_household}
                      onChange={(e) => setLpcdSettings({...lpcdSettings, persons_per_household: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">Average family size</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => handleSaveSettings('NRW Thresholds')} disabled={isLoading}>
                  <Save className="w-4 h-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={loadSystemSettings} disabled={isLoading}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Saved
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alert Settings for Demo */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Alert Configuration</CardTitle>
              <CardDescription>Configure alert settings for demos and client presentations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                  <div>
                    <h4 className="font-medium">NRW Alerts</h4>
                    <p className="text-sm text-muted-foreground">Alert when NRW exceeds threshold</p>
                  </div>
                  <Switch
                    checked={alertSettings.enable_nrw_alerts}
                    onCheckedChange={(checked) => setAlertSettings({...alertSettings, enable_nrw_alerts: checked})}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                  <div>
                    <h4 className="font-medium">Water Quality Alerts</h4>
                    <p className="text-sm text-muted-foreground">Alert on water quality parameter violations</p>
                  </div>
                  <Switch
                    checked={alertSettings.enable_quality_alerts}
                    onCheckedChange={(checked) => setAlertSettings({...alertSettings, enable_quality_alerts: checked})}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                  <div>
                    <h4 className="font-medium">Maintenance Alerts</h4>
                    <p className="text-sm text-muted-foreground">Schedule and track maintenance activities</p>
                  </div>
                  <Switch
                    checked={alertSettings.enable_maintenance_alerts}
                    onCheckedChange={(checked) => setAlertSettings({...alertSettings, enable_maintenance_alerts: checked})}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                  <div>
                    <h4 className="font-medium">Leak Detection Alerts</h4>
                    <p className="text-sm text-muted-foreground">Alert on suspected pipeline leaks</p>
                  </div>
                  <Switch
                    checked={alertSettings.enable_leak_alerts}
                    onCheckedChange={(checked) => setAlertSettings({...alertSettings, enable_leak_alerts: checked})}
                  />
                </div>
              </div>

              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">Alert Thresholds & Timing</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nrw-alert-threshold">NRW Alert Threshold (%)</Label>
                    <Input
                      id="nrw-alert-threshold"
                      type="number"
                      step="0.1"
                      value={alertSettings.nrw_alert_threshold}
                      onChange={(e) => setAlertSettings({...alertSettings, nrw_alert_threshold: parseFloat(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">Trigger alert above this NRW %</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quality-delay">Quality Alert Delay (hours)</Label>
                    <Input
                      id="quality-delay"
                      type="number"
                      value={alertSettings.quality_alert_delay_hours}
                      onChange={(e) => setAlertSettings({...alertSettings, quality_alert_delay_hours: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">Hours before quality alert</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maintenance-days">Maintenance Notice (days)</Label>
                    <Input
                      id="maintenance-days"
                      type="number"
                      value={alertSettings.maintenance_notification_days}
                      onChange={(e) => setAlertSettings({...alertSettings, maintenance_notification_days: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-muted-foreground">Days before scheduled maintenance</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-900 dark:text-yellow-100">
                  <strong>Demo Mode:</strong> These settings control alert generation for client demonstrations. 
                  Alerts will be generated based on configured thresholds and village data.
                </p>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => handleSaveSettings('Alert Settings')} disabled={isLoading}>
                  <Save className="w-4 h-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={loadSystemSettings} disabled={isLoading}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Saved
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security tab for password change */}
        <TabsContent value="security" className="space-y-4">
          <ChangePassword />
        </TabsContent>

        {/* System tab placeholder */}
        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Information</CardTitle>
              <CardDescription>View system status and configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">System Name</p>
                    <p className="font-medium">SWATI Dashboard</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Region</p>
                    <p className="font-medium">Salcette Block, Goa</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Villages</p>
                    <p className="font-medium">{Object.keys(villageData).length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Database Status</p>
                    <Badge variant="default">Connected</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Database Reseed Section */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive">Database Reset</CardTitle>
              <CardDescription>
                Reset database to default Salcette Block configuration. This will remove all custom data and restore default villages and settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive font-medium mb-2">⚠️ Warning: This action cannot be undone!</p>
                  <p className="text-sm text-muted-foreground">
                    Clicking "Reseed Database" will:
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                    <li>Delete all existing village data</li>
                    <li>Restore 5 default villages (Cortalim, Verna, Margao, Benaulim, Colva)</li>
                    <li>Reset NRW thresholds and LPCD settings to defaults</li>
                    <li>Clear custom configurations</li>
                  </ul>
                </div>
                
                <Button
                  variant="destructive"
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to reseed the database? This will delete all current data and cannot be undone!')) {
                      try {
                        const storedAuth = localStorage.getItem('swati_auth');
                        if (!storedAuth) {
                          toast.error('Authentication required');
                          return;
                        }
                        
                        const authData = JSON.parse(storedAuth);
                        const token = authData.token;
                        
                        const response = await fetch(`${API_URL}/api/system/reseed`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          }
                        });
                        
                        const data = await response.json();
                        
                        if (response.ok && data.success) {
                          toast.success('Database reseeded successfully!');
                          // Reload villages data
                          loadVillages();
                          loadNRWSettings();
                          loadLPCDSettings();
                        } else {
                          toast.error(data.detail || 'Failed to reseed database');
                        }
                      } catch (error) {
                        console.error('Reseed error:', error);
                        toast.error('Failed to reseed database');
                      }
                    }
                  }}
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reseed Database to Defaults
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;